// @ts-nocheck
import { useCallback, useRef } from "react";
import { Box } from "@mui/material";

const MIN_PX = 60;
const MAX_PX = 800;

/**
 * Resize handle for table columns.
 *
 * Anti-lag optimization: during the drag, the width is updated via direct DOM
 * (`th.style.width`) — instantaneous, zero re-render. It only commits to state
 * (= localStorage + React state) on mouseup. Previously, calling setState on every
 * mousemove caused a re-render of the entire table per pixel → the perception of
 * "stutters, doesn't follow the cursor".
 *
 * Props:
 *  - onResize(px): committed on mouseup, triggers persist.
 *  - onReset(): double-click → reset to default.
 */
const ResizeHandle = ({ onResize, onReset }) => {
  const dragState = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!dragState.current) return;
    const { startX, startWidth, th, bodyCells } = dragState.current;
    const next = Math.min(
      MAX_PX,
      Math.max(MIN_PX, startWidth + (e.clientX - startX)),
    );
    // Direct DOM mutation — instantaneous visual feedback, avoids React re-render
    // during the drag (which was the cause of the "doesn't follow the cursor" issue).
    th.style.width = `${next}px`;
    th.style.minWidth = `${next}px`;
    for (const cell of bodyCells) {
      cell.style.width = `${next}px`;
      cell.style.minWidth = `${next}px`;
    }
    dragState.current.lastWidth = next;
  }, []);

  const handleMouseUp = useCallback(() => {
    const final = dragState.current?.lastWidth;
    dragState.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
    if (final != null && typeof onResize === "function") {
      // Final commit to React state → triggers persist to localStorage.
      onResize(final);
    }
  }, [handleMouseMove, onResize]);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const th = e.currentTarget.parentElement;
      if (!th) return;
      const startWidth = th.getBoundingClientRect().width;
      // Collect the matching cells in the body (same index) to update them
      // together. If they can't be found, only the header is updated (degraded).
      const table = th.closest("table");
      const thRow = th.parentElement;
      const thIdx = thRow ? Array.from(thRow.children).indexOf(th) : -1;
      const bodyCells =
        table && thIdx >= 0
          ? Array.from(table.querySelectorAll("tbody tr")).map(
              (row) => row.children[thIdx],
            ).filter(Boolean)
          : [];
      dragState.current = {
        startX: e.clientX,
        startWidth,
        lastWidth: startWidth,
        th,
        bodyCells,
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [handleMouseMove, handleMouseUp],
  );

  const handleDoubleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onReset) onReset();
    },
    [onReset],
  );

  return (
    <Box
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        // More generous hit area (12px) — it used to be 8px and was hard to grab.
        width: "12px",
        cursor: "col-resize",
        userSelect: "none",
        zIndex: 10,
        "&::after": {
          content: '""',
          position: "absolute",
          top: "25%",
          bottom: "25%",
          right: "5px",
          width: "2px",
          backgroundColor: "rgba(255, 255, 255, 0.35)",
          borderRadius: "1px",
          transition: "background-color 120ms ease",
        },
        "&:hover::after, &:active::after": {
          backgroundColor: "#D175B6",
          top: "15%",
          bottom: "15%",
          width: "3px",
        },
      }}
    />
  );
};

export default ResizeHandle;
